/**
 * MÜŞTERİ ERİŞİMİ PANELİ (2026-08-20) — Ayarlar > Müşteri Erişimi.
 * Aktif siteyi bir müşterinin e-postasına açar: kişi yalnız BU siteyi görür ve
 * kullanır (sahibin paketi üzerinden; ayrı paket gerekmez). Kişi kayıtlıysa anında
 * bağlanır; değilse kayıt olup onaylandıktan sonra ilk girişinde otomatik bağlanır.
 * Yalnız site SAHİBİ yönetebilir — misafir bu ekranda yalnız bilgi görür.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useListDomains } from "@workspace/api-client-react";
import { useActiveDomain } from "@/hooks/use-active-domain";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useOnay } from "@/components/ui/onay-dialog";
import { Loader2, UserPlus, X, Users, CheckCircle2, Clock } from "lucide-react";

type Paylasim = { id: number; email: string; userId: number | null; createdAt: string; durum: "bagli" | "kayit-bekliyor" };

export function ErisimPanel() {
  const { data: domains } = useListDomains();
  const [activeDomainId] = useActiveDomain(domains);
  const { toast } = useToast();
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const { onayIste, OnayPenceresi } = useOnay();

  const liste = useQuery<{ paylasimlar: Paylasim[]; error?: string }>({
    queryKey: ["erisim", activeDomainId],
    enabled: !!activeDomainId,
    queryFn: async () => {
      const r = await fetch(`/api/domains/${activeDomainId}/erisim`);
      const j = await r.json();
      if (r.status === 403) return { paylasimlar: [], error: "sahip-degil" };
      return j;
    },
  });

  const ekle = useMutation({
    mutationFn: async () => {
      const r = await fetch(`/api/domains/${activeDomainId}/erisim`, {
        method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ email }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error ?? "Eklenemedi");
      return j as { mesaj: string };
    },
    onSuccess: (j) => {
      toast({ title: "Erişim eklendi ✓", description: j.mesaj });
      setEmail("");
      void qc.invalidateQueries({ queryKey: ["erisim", activeDomainId] });
    },
    onError: (e) => toast({ title: "Eklenemedi", description: String(e instanceof Error ? e.message : e), variant: "destructive" }),
  });

  const kaldir = useMutation({
    mutationFn: async (aid: number) => {
      const r = await fetch(`/api/domains/${activeDomainId}/erisim/${aid}`, { method: "DELETE" });
      if (!r.ok) throw new Error("Kaldırılamadı");
    },
    onSuccess: () => { toast({ title: "Erişim kaldırıldı" }); void qc.invalidateQueries({ queryKey: ["erisim", activeDomainId] }); },
  });

  if (liste.isLoading) return <Card className="p-8 text-center text-muted-foreground"><Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />Okunuyor…</Card>;
  if (liste.data?.error === "sahip-degil") {
    return <Card className="p-6 text-[13px] text-muted-foreground">Bu site sana paylaşımla açılmış — erişim yönetimi yalnız site sahibinde.</Card>;
  }
  const aktifSite = domains?.find((d) => d.id === activeDomainId)?.url?.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "") ?? "";

  return (
    <div className="space-y-4">
      {OnayPenceresi}
      <Card className="p-4">
        <div className="mb-1 flex items-center gap-2 text-[13.5px] font-semibold"><Users className="h-4 w-4" /> {aktifSite} sitesine müşteri erişimi ver</div>
        <p className="mb-3 max-w-2xl text-[12px] text-muted-foreground">
          Eklediğin e-posta yalnız BU siteyi görür ve kullanır — diğer sitelerine erişemez, senin paketin üzerinden çalışır
          (ayrı paket gerekmez, harcamalar senin aylık hakkından düşer). Kişi Geolop'ta kayıtlı değilse:
          platform.geolop.com'da bu e-postayla <b>Üye Ol</b> der, başvurusunu admin panelinden onaylarsın, ilk girişinde site kendiliğinden bağlanır.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="musteri@ornek.com" type="email"
            onKeyDown={(e) => { if (e.key === "Enter" && email.trim()) ekle.mutate(); }}
            className="w-72 rounded-lg border bg-transparent px-3 py-2 text-[13px] outline-none dark:border-white/15" />
          <Button className="gap-2 bg-brand-500 hover:bg-brand-600" disabled={ekle.isPending || !email.trim()} onClick={() => ekle.mutate()}>
            {ekle.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Erişim Ver
          </Button>
        </div>
      </Card>

      {(liste.data?.paylasimlar?.length ?? 0) > 0 && (
        <Card className="p-4">
          <div className="mb-2 text-[12.5px] font-semibold">Erişimi olanlar ({liste.data!.paylasimlar.length})</div>
          <div className="space-y-1.5">
            {liste.data!.paylasimlar.map((p) => (
              <div key={p.id} className="flex items-center gap-2 text-[12.5px]">
                {p.durum === "bagli"
                  ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                  : <Clock className="h-3.5 w-3.5 text-amber-500" />}
                <span className="font-medium">{p.email}</span>
                <span className={`rounded-full px-1.5 py-0.5 text-[9.5px] font-bold ${p.durum === "bagli" ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" : "bg-amber-500/15 text-amber-600 dark:text-amber-400"}`}>
                  {p.durum === "bagli" ? "BAĞLI" : "KAYIT BEKLİYOR"}
                </span>
                <span className="ml-auto text-[11px] text-muted-foreground">{new Date(p.createdAt).toLocaleDateString("tr-TR")}</span>
                <button onClick={() => { void onayIste({ baslik: "Erişim kaldırılsın mı?", aciklama: `${p.email} bu siteye artık giremeyecek.`, onayYazisi: "Kaldır", tehlikeli: true }).then((t) => { if (t) kaldir.mutate(p.id); }); }}
                  className="rounded-md p-1 text-gray-400 transition hover:bg-gray-100 hover:text-red-500 dark:hover:bg-white/[0.06]" title="Erişimi kaldır">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
